import React from 'react';

import type { ColorThemeId } from 'types/settings';

import config from 'configs/app';
import * as cookies from 'lib/cookies';
import { COLOR_THEMES, getThemeHexWithOverrides } from 'lib/settings/colorTheme';
import type { ColorMode } from 'toolkit/chakra/color-mode';
import { useColorMode } from 'toolkit/chakra/color-mode';
import { IconButton } from 'toolkit/chakra/icon-button';
import { Tooltip } from 'toolkit/chakra/tooltip';
import IconSvg from 'ui/shared/IconSvg';

const THEME_BY_MODE: Record<ColorMode, ColorThemeId> = {
  light: 'light',
  dark: 'dark',
};

const Settings = () => {
  const { setColorMode } = useColorMode();
  const [ mode, setMode ] = React.useState<ColorMode>();

  const applyMode = React.useCallback((nextMode: ColorMode) => {
    const themeId = THEME_BY_MODE[nextMode];
    const theme = COLOR_THEMES.find((item) => item.id === themeId);
    const varValue = getThemeHexWithOverrides(themeId);

    if (!theme || !varValue) {
      return;
    }

    setColorMode(nextMode);

    const varName = nextMode === 'light' ? '--chakra-colors-white' : '--chakra-colors-black';
    window.document.documentElement.style.setProperty(varName, varValue);

    cookies.set(cookies.NAMES.COLOR_MODE, nextMode);
    cookies.set(cookies.NAMES.COLOR_THEME, themeId);
    window.localStorage.setItem(cookies.NAMES.COLOR_MODE, nextMode);
    setMode(nextMode);
  }, [ setColorMode ]);

  React.useEffect(() => {
    const cookieColorMode = cookies.get(cookies.NAMES.COLOR_MODE) as ColorMode | undefined;
    const initialMode = cookieColorMode === 'dark' || cookieColorMode === 'light' ?
      cookieColorMode :
      config.UI.colorTheme.default?.colorMode ?? 'light';
    applyMode(initialMode);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ ]);

  const handleClick = React.useCallback(() => {
    applyMode(mode === 'dark' ? 'light' : 'dark');
  }, [ applyMode, mode ]);

  const label = mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';

  return (
    <Tooltip content={ label } disableOnMobile>
      <IconButton
        variant="link"
        size="2xs"
        borderRadius="sm"
        aria-label={ label }
        onClick={ handleClick }
      >
        <IconSvg name={ mode === 'dark' ? 'sun' : 'moon' }/>
      </IconButton>
    </Tooltip>
  );
};

export default React.memo(Settings);
